import { IconProps } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'

import { useOutsideClick } from '../hooks/useOutsideClick'
import {
  MangoButton,
  MangoButtonVariant,
  type SVGComponent,
} from '../MangoButton'
import { Tooltip } from '../ui/Tooltip'
import { hasValue } from '../utils'
import { castRef } from '../utils'
import { Menu, MenuOptions, MenuPositioningWrapper } from './Menu'
import { MenuItem } from './MenuItem'

export type MangoButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type DropdownMangoButtonProps<TValue> = {
  variant: MangoButtonVariant
  tooltipLabel?: string | React.ReactNode
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  onButtonClick?: () => void
  items: MangoButtonMenuItem<TValue>[]
  selectedIndex?: number
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => void
  menuOptions?: MenuOptions
  mangoButtonOptions?: {
    leftIconProps?: IconProps
    rightIconProps?: IconProps
  }
} & (
  | { label: string; placeholder?: never }
  | { label?: never; placeholder: string }
)

export const DropdownMangoButton = <TValue,>({
  label,
  placeholder,
  variant,
  tooltipLabel,
  leftIcon,
  rightIcon,
  onButtonClick,
  items,
  selectedIndex,
  onSelect,
  onMenuOpenChange,
  menuOptions,
  mangoButtonOptions,
}: DropdownMangoButtonProps<TValue>) => {
  const [internalIndex, setInternalIndex] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = useState(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (menuOpen) {
        setMenuOpen(false)
        onMenuOpenChange?.(false)
      }
    },
  })

  const buttonLabel = useMemo(() => {
    const index = selectedIndex ?? internalIndex
    const selectedItem = items[index]

    return selectedItem?.buttonLabel ?? label ?? placeholder
  }, [items, selectedIndex, internalIndex])

  const handleButtonClick = () => {
    setMenuOpen((prev) => !prev)
    onMenuOpenChange?.(!menuOpen)
    onButtonClick?.()
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setInternalIndex(index)
    setMenuOpen(false)
    onMenuOpenChange?.(false)
  }

  return (
    <MenuPositioningWrapper ref={containerRef}>
      <Tooltip
        showArrow
        content={tooltipLabel}
        positioning={{ placement: 'top' }}
        disabled={!hasValue(tooltipLabel)}
      >
        <MangoButton
          ref={buttonRef}
          variant={variant}
          leftIcon={leftIcon}
          leftIconProps={mangoButtonOptions?.leftIconProps}
          rightIcon={rightIcon}
          rightIconProps={mangoButtonOptions?.rightIconProps}
          onClick={handleButtonClick}
          iconStrokeWidth={
            (mangoButtonOptions?.leftIconProps?.strokeWidth ??
              mangoButtonOptions?.rightIconProps?.strokeWidth ??
              '2px') as string
          }
        >
          {buttonLabel}
        </MangoButton>
      </Tooltip>
      {menuOpen && items.length > 0 && (
        <Menu
          anchorRef={castRef(buttonRef)}
          items={items}
          onSelect={handleSelect}
          options={menuOptions}
        />
      )}
    </MenuPositioningWrapper>
  )
}
